import React from "react";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
// Material UI imports
import {Checkbox, Table, TableBody, TableCell, TableRow} from "@material-ui/core";
// Project imports
import API, {createApiConfig} from "../../../../utils/API";
import {AxiosError} from "axios";
import {TrackedIndicatorGroupSimpleDTO, TrackedIndicatorGroupUpdateDTO} from "../../../../models/trackedIndicators";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import {EditActionButton, SaveActionButton} from "../../../common/buttons";
import Loading from "../../../common/Loading";
import {ErrorNotification, SuccessNotification} from "../../../common/notifications";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            maxWidth: "50em",
        },
        labelCell: {
            border: "0",
        },
        contentCell: {
            border: "0",
            width: "100%",
            paddingBottom: 0,
            paddingTop: 0,
        },
        actionsCell: {
            border: "0",
            paddingBottom: 0,
            paddingTop: 0,
        },
    })
);

export interface TrackedIndicatorGroupDetailsEditProps {
    trackedIndicatorGroup: TrackedIndicatorGroupSimpleDTO;
    showParentFlags?: boolean;
    onChange(trackedIndicatorGroup: TrackedIndicatorGroupSimpleDTO): void;
}

const GroupDetailsEdit: React.FC<TrackedIndicatorGroupDetailsEditProps> = (
    props: TrackedIndicatorGroupDetailsEditProps
) => {
    const classes = useStyles();
    const [t] = useTranslation("trackedIndicators");
    const {keycloak, initialized} = useKeycloak();
    const [state, setState] = React.useState({
        trackedIndicatorGroup: props.trackedIndicatorGroup,
        name: "init" as "init" | "saved" | "savingName" | "savingDescription" | "savingFlags" | "failed",
    });
    const [nameField, setNameField] = React.useState({
        edit: false,
        content: state.trackedIndicatorGroup.name,
    });
    const [descriptionField, setDescriptionField] = React.useState({
        edit: false,
        content: state.trackedIndicatorGroup.description,
    });
    //== Effects ================================================================
    React.useEffect(() => {
        if (state.name === "savingName" || state.name === "savingDescription" || state.name === "savingFlags") {
            const newTrackedIndicatorGroup: TrackedIndicatorGroupUpdateDTO = {
                ...state.trackedIndicatorGroup,
                name: state.name === "savingName" ? nameField.content : state.trackedIndicatorGroup.name,
                description:
                    state.name === "savingDescription"
                        ? descriptionField.content
                        : state.trackedIndicatorGroup.description,
            };
            API.put<TrackedIndicatorGroupSimpleDTO>(
                `/tracked-indicator-groups/simple/${props.trackedIndicatorGroup.id}`,
                newTrackedIndicatorGroup,
                createApiConfig(keycloak, initialized)
            )
                .then((res) => {
                    setState({
                        ...state,
                        trackedIndicatorGroup: res.data,
                        name: "saved",
                    });
                    if (state.name === "savingName") setNameField({edit: false, content: res.data.name});
                    else if (state.name === "savingDescription")
                        setDescriptionField({edit: false, content: res.data.description});
                    props.onChange(res.data);
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "failed"});
                });
        }
        if (
            props.trackedIndicatorGroup !== state.trackedIndicatorGroup &&
            state.name !== "savingName" &&
            state.name !== "savingDescription" &&
            state.name !== "savingFlags"
        ) {
            setState({...state, trackedIndicatorGroup: props.trackedIndicatorGroup});
            setNameField({edit: false, content: props.trackedIndicatorGroup.name});
            setDescriptionField({edit: false, content: props.trackedIndicatorGroup.description});
        }
    }, [keycloak, initialized, state, setState, props.trackedIndicatorGroup]);

    const handleNameEdit = () => {
        setNameField({...nameField, edit: true});
    };
    const handleNameSave = () => {
        setState({...state, name: "savingName"});
    };

    const handleDescriptionEdit = () => {
        setDescriptionField({...descriptionField, edit: true});
    };
    const handleDescriptionSave = () => {
        setState({...state, name: "savingDescription"});
    };

    const handleParentReadChange = (checked: boolean) => {
        const next = {
            ...state.trackedIndicatorGroup,
            parentRead: checked,
            parentEdit: checked ? state.trackedIndicatorGroup.parentEdit : false,
        };
        setState({...state, trackedIndicatorGroup: next, name: "savingFlags"});
    };

    const handleParentEditChange = (checked: boolean) => {
        const next = {...state.trackedIndicatorGroup, parentEdit: checked};
        setState({...state, trackedIndicatorGroup: next, name: "savingFlags"});
    };

    const isSaving = state.name === "savingName" || state.name === "savingDescription" || state.name === "savingFlags";

    let notification: JSX.Element | null = null;
    if (state.name === "saved") {
        notification = <SuccessNotification message={t(`trackedIndicatorGroupDetailEditNotifications.save_ok`)} />;
    } else if (state.name === "failed") {
        notification = <ErrorNotification message={t(`trackedIndicatorGroupDetailEditNotifications.save_fail`)} />;
    }
    return (
        <>
            <Table className={classes.table}>
                <TableBody>
                    <TableRow>
                        <TableCell className={classes.labelCell}>{t("trackedIndicatorGroup.name")}:</TableCell>
                        <TableCell className={classes.contentCell}>
                            {state.name === "savingName" ? (
                                <Loading />
                            ) : nameField.edit ? (
                                <TextField
                                    id="name"
                                    value={nameField.content}
                                    fullWidth
                                    required
                                    onChange={(e): void =>
                                        setNameField({
                                            ...nameField,
                                            content: e.target.value,
                                        })
                                    }
                                />
                            ) : (
                                nameField.content
                            )}
                        </TableCell>
                        <TableCell className={classes.actionsCell} align="left">
                            {state.name === "savingName" ? null : nameField.edit ? (
                                <SaveActionButton onClick={() => handleNameSave()} />
                            ) : (
                                <EditActionButton onClick={() => handleNameEdit()} />
                            )}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.labelCell}>{t("trackedIndicatorGroup.description")}:</TableCell>
                        <TableCell className={classes.contentCell}>
                            {state.name === "savingDescription" ? (
                                <Loading />
                            ) : descriptionField.edit ? (
                                <TextField
                                    id="description"
                                    value={descriptionField.content === null ? "" : descriptionField.content}
                                    fullWidth
                                    required
                                    onChange={(e): void =>
                                        setDescriptionField({
                                            ...descriptionField,
                                            content: e.target.value === "" ? null : e.target.value,
                                        })
                                    }
                                />
                            ) : (
                                descriptionField.content
                            )}
                        </TableCell>
                        <TableCell className={classes.actionsCell} align="left">
                            {state.name === "savingDescription" ? null : descriptionField.edit ? (
                                <SaveActionButton onClick={() => handleDescriptionSave()} />
                            ) : (
                                <EditActionButton onClick={() => handleDescriptionEdit()} />
                            )}
                        </TableCell>
                    </TableRow>
                    {props.showParentFlags && (
                        <>
                            <TableRow>
                                <TableCell className={classes.labelCell} style={{whiteSpace: "nowrap"}}>
                                    {t("trackedIndicatorGroup.parentRead")}:
                                </TableCell>
                                <TableCell className={classes.contentCell} colSpan={2}>
                                    <Checkbox
                                        checked={state.trackedIndicatorGroup.parentRead}
                                        disabled={isSaving}
                                        color="primary"
                                        onChange={(e) => handleParentReadChange(e.target.checked)}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.labelCell} style={{whiteSpace: "nowrap"}}>
                                    {t("trackedIndicatorGroup.parentEdit")}:
                                </TableCell>
                                <TableCell className={classes.contentCell} colSpan={2}>
                                    <Checkbox
                                        checked={state.trackedIndicatorGroup.parentEdit}
                                        disabled={isSaving || !state.trackedIndicatorGroup.parentRead}
                                        color="primary"
                                        onChange={(e) => handleParentEditChange(e.target.checked)}
                                    />
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                </TableBody>
            </Table>
            {notification}
        </>
    );
};

export default GroupDetailsEdit;
